/**
 * Phase L.1 — backfill typed `DynamicBlockMetadata` for legacy blocks
 * that arrive from the DXF parser carrying raw `customProperties` but
 * no `dynamicMetadata.parameters[]`.
 *
 * Source-of-truth context:
 *   - K.5 (`feat(block-editor): Phase K …`) shipped DXF authoring round-
 *     trip so blocks created in the new authoring palette persist with
 *     full `dynamicMetadata`.
 *   - Older blocks (and 3rd-party DXFs) still flow in with raw English
 *     keys like `Position1`, `VisibilityState1`, `FlipState1`, …
 *     and `dynamicMetadata` either absent OR with empty parameters.
 *   - Without typed metadata the Inspector falls back to the `inferType
 *     FromValue` heuristic in `dynamicBlockMutations.ts:listDynamicParameters`
 *     which only detects `distance` / `flip` / `unknown`. Linear /
 *     Visibility / Polar / Rotation / Lookup all collapse to plain text
 *     inputs with raw English labels.
 *
 * What this module does:
 *   - Pattern-matches well-known AutoCAD raw names against parameter
 *     kinds (xy / point / visibility / flip / polar / linear / rotation
 *     / lookup), preserving the customProperties insertion order.
 *   - Synthesises a `DynamicBlockMetadata` shape that the existing
 *     `listDynamicParameters` / `paramInfoFromDynamicParameter` /
 *     `boundsForParam` pipelines accept verbatim — the Inspector then
 *     renders typed widgets (slider for linear, dropdown for
 *     visibility, checkbox for flip, …) instead of raw text inputs.
 *   - Fills in DUMMY geometry fields (`basePoint`, `secondPoint`,
 *     `position`, etc.) because the legacy DXF carried only the
 *     customProperties scalars — there is no authored grip placement
 *     to recover. The dummies are zeroed; rendering of grips on
 *     synthesised parameters is an out-of-scope follow-up (Phase L.1
 *     ships UI-level typed labels + widgets only, not new grips).
 *
 * Preservation contract:
 *   - When `dynamicMetadata` already exists with non-empty
 *     `parameters[]`, we return UNDEFINED — caller keeps the original
 *     metadata. Synthesis never overrides explicit author data.
 *
 * Naming convention notes:
 *   - The Russian labels follow AutoCAD CIS terminology (Координата /
 *     Видимость / Отражение / …) which matches the i18n labels in
 *     `apps/web/src/app/i18n/dynamicBlockLabels.ts`.
 *   - Synthesised parameter `id` collides with `name` deterministically
 *     (`syn-<key>`) so the writer / round-trip never sees clashing ids
 *     even when multiple parameters share a base.
 */

import type {
  BlockDefinition,
  DynamicBlockMetadata,
  DynamicParameter,
  ValueSet,
} from "@dxf-ai-editor/drawing-model";

/**
 * Shape of the customProperties bag carried by a block INSERT entity.
 * Mirrors `BlockInsertEntity.customProperties` in drawing-model.
 */
type CustomProps = Readonly<Record<string, string | number | boolean>>;

/**
 * Default empty value set — `mode: "none"` lets the Inspector slider
 * fall back to its `0…2×currentValue` heuristic in
 * `dynamicBlockControls.computeRange`. We do NOT synthesise increments
 * or list values because the source DXF carried no constraint — making
 * one up would lock users out of legitimate edits.
 */
const EMPTY_VALUE_SET: ValueSet = {
  mode: "none",
  increment: 0,
  listValues: [],
};

const EMPTY_POINT = { x: 0, y: 0 } as const;

/**
 * Decide whether a block needs metadata backfill.
 *
 * Returns `true` when:
 *   - the block has zero `dynamicMetadata` parameters (either undefined
 *     or empty array), AND
 *   - the INSERT carries at least one customProperty.
 *
 * Returns `false` when typed metadata already exists, OR when there
 * are no customProperties to project (purely static block).
 */
export function shouldBackfill(
  blockDef: BlockDefinition | undefined,
  customProperties: CustomProps | undefined,
): boolean {
  if (!customProperties || Object.keys(customProperties).length === 0) {
    return false;
  }
  if (!blockDef?.dynamicMetadata) return true;
  return blockDef.dynamicMetadata.parameters.length === 0;
}

/**
 * Build a `BlockDefinition` whose `dynamicMetadata.parameters[]` has
 * been synthesised from the entity's customProperties keys. The input
 * `blockDef` is left untouched (returns a SHALLOW clone with a fresh
 * `dynamicMetadata` field). When `shouldBackfill` returns false, the
 * input is returned verbatim.
 *
 * Caller responsibilities:
 *   - The synthesised parameters carry minimum-viable geometry
 *     (zeroed basePoint / secondPoint / etc.). Action evaluation is
 *     intentionally NOT supported — DYNSET / DYNRESET still operate on
 *     the customProperties bag directly.
 *   - The `defaultStateId` / `currentStateId` for synthesised
 *     visibility parameters are derived from the customProperty's
 *     current value — guarantees the dropdown's selected option
 *     matches the entity state on first render.
 */
export function backfillBlockDynamicMetadata(
  blockDef: BlockDefinition | undefined,
  customProperties: CustomProps | undefined,
): BlockDefinition | undefined {
  if (!blockDef) return blockDef;
  if (!shouldBackfill(blockDef, customProperties)) return blockDef;
  const inferred = inferDynamicMetadata(customProperties ?? {});
  if (!inferred || inferred.parameters.length === 0) return blockDef;
  // Preserve any pre-existing dynamicMetadata fields (futureExtensions,
  // grips, etc.) so the writer's round-trip stays lossless.
  const existing = blockDef.dynamicMetadata;
  const merged: DynamicBlockMetadata = existing
    ? { ...existing, parameters: inferred.parameters }
    : inferred;
  return { ...blockDef, dynamicMetadata: merged };
}

/**
 * Walk the customProperties keys in insertion order and synthesise
 * typed `DynamicParameter` entries. Compound suffixed keys
 * (`<base>.x` / `<base>.y` / `<base>.distance` / `<base>.angle`) are
 * preserved as-is — the Inspector's `dynamicBlockCompoundGrouping`
 * helper recognises them when the parent metadata declares the
 * compound kind, but we have no authored parent in the legacy case so
 * they fall through as scalar parameters with translated labels.
 *
 * Pattern matrix (in order of detection):
 *   1. `Position{N} {X|Y}`  → XY parameter (one row, two sub-fields).
 *   2. `Position{N}` (singular) → Point parameter.
 *   3. `VisibilityState{N}` / `Visibility{N}` → Visibility parameter.
 *   4. `FlipState{N}` / `Flip{N}` → Flip parameter.
 *   5. `Distance{N}` paired with `Angle{N}` → Polar parameter.
 *      Otherwise standalone Linear parameter.
 *   6. `Angle{N}` (without preceding Distance) → standalone Rotation.
 *   7. `Rotation{N}` → Rotation parameter.
 *   8. `Lookup{N}` → Lookup parameter.
 *   9. Unknown → fallback to a Linear parameter when value is numeric,
 *      Flip when boolean, otherwise skipped (string-only values stay
 *      visible via the raw-mode label translator in
 *      `dynamicBlockLabels.ts`).
 */
export function inferDynamicMetadata(
  props: CustomProps,
): DynamicBlockMetadata {
  const parameters: DynamicParameter[] = [];
  const seen = new Set<string>();
  const synId = (key: string): string => `syn-${key}`;
  const numericValue = (key: string): number => {
    const v = props[key];
    return typeof v === "number" ? v : Number.parseFloat(String(v ?? 0)) || 0;
  };
  const booleanValue = (key: string): boolean => {
    const v = props[key];
    if (typeof v === "boolean") return v;
    const s = String(v ?? "").trim().toLowerCase();
    return s === "true" || s === "1" || s === "yes";
  };
  const stringValue = (key: string): string => {
    const v = props[key];
    return v == null ? "" : String(v);
  };

  for (const key of Object.keys(props)) {
    if (seen.has(key)) continue;

    // ── 1. Position{N} {X|Y} ──────────────────────────────────────
    // Both X and Y must be present to form an XY parameter; missing
    // sibling demotes to a singleton Point parameter (handled below).
    const xyAxisMatch = key.match(/^Position(\d+)\s+([XY])$/);
    if (xyAxisMatch) {
      const idx = xyAxisMatch[1]!;
      const xKey = `Position${idx} X`;
      const yKey = `Position${idx} Y`;
      if (xKey in props && yKey in props) {
        parameters.push({
          kind: "xy",
          id: synId(`Position${idx}`),
          name: `Position${idx}`,
          label: "",
          description: "",
          chainActions: false,
          showGrip: false,
          basePoint: EMPTY_POINT,
          secondPoint: EMPTY_POINT,
          defaultX: numericValue(xKey),
          defaultY: numericValue(yKey),
          currentX: numericValue(xKey),
          currentY: numericValue(yKey),
          xValueSet: { ...EMPTY_VALUE_SET },
          yValueSet: { ...EMPTY_VALUE_SET },
        });
        seen.add(xKey);
        seen.add(yKey);
        continue;
      }
      // Fall through: only one axis present — treat it as a generic
      // linear parameter via the unknown branch below. Mark seen so we
      // don't re-detect the partner.
    }

    // ── 2. Position{N} (singular) → Point ─────────────────────────
    if (/^Position\d+$/.test(key)) {
      parameters.push({
        kind: "point",
        id: synId(key),
        name: key,
        label: "",
        description: "",
        chainActions: false,
        showGrip: false,
        basePoint: EMPTY_POINT,
        currentPoint: EMPTY_POINT,
        xValueSet: { ...EMPTY_VALUE_SET },
        yValueSet: { ...EMPTY_VALUE_SET },
      });
      seen.add(key);
      continue;
    }

    // ── 3. Visibility{N} / VisibilityState{N} ─────────────────────
    if (/^(VisibilityState|Visibility)\d+$/.test(key)) {
      const current = stringValue(key) || "Default";
      parameters.push({
        kind: "visibility",
        id: synId(key),
        name: key,
        label: "",
        description: "",
        chainActions: false,
        showGrip: false,
        position: EMPTY_POINT,
        // Synthesise a one-state list so zod's `.min(1)` constraint
        // passes; the dropdown UI surfaces just the current state.
        states: [
          {
            id: current,
            name: current,
            invisibleEntityIds: [],
          },
        ],
        defaultStateId: current,
        currentStateId: current,
      });
      seen.add(key);
      continue;
    }

    // ── 4. Flip{N} / FlipState{N} ─────────────────────────────────
    if (/^(FlipState|Flip)\d+$/.test(key)) {
      const current = booleanValue(key);
      parameters.push({
        kind: "flip",
        id: synId(key),
        name: key,
        label: "",
        description: "",
        chainActions: false,
        showGrip: false,
        axisStart: EMPTY_POINT,
        axisEnd: EMPTY_POINT,
        defaultValue: current,
        currentValue: current,
        flipLabel: "Flip",
        baseLabel: "Base",
      });
      seen.add(key);
      continue;
    }

    // ── 5. Distance{N} (+optional Angle{N}) → Polar / Linear ──────
    const distMatch = key.match(/^Distance(\d+)$/);
    if (distMatch) {
      const idx = distMatch[1]!;
      const angleKey = `Angle${idx}`;
      const distance = numericValue(key);
      if (angleKey in props) {
        const angle = numericValue(angleKey);
        parameters.push({
          kind: "polar",
          id: synId(`Distance${idx}`),
          name: `Distance${idx}`,
          label: "",
          description: "",
          chainActions: false,
          showGrip: false,
          basePoint: EMPTY_POINT,
          secondPoint: EMPTY_POINT,
          defaultDistance: distance,
          defaultAngle: angle,
          currentDistance: distance,
          currentAngle: angle,
          distanceValueSet: { ...EMPTY_VALUE_SET },
          angleValueSet: { ...EMPTY_VALUE_SET },
        });
        seen.add(key);
        seen.add(angleKey);
        continue;
      }
      parameters.push({
        kind: "linear",
        id: synId(key),
        name: key,
        label: "",
        description: "",
        chainActions: false,
        showGrip: false,
        basePoint: EMPTY_POINT,
        secondPoint: EMPTY_POINT,
        defaultDistance: distance,
        currentDistance: distance,
        valueSet: { ...EMPTY_VALUE_SET },
      });
      seen.add(key);
      continue;
    }

    // ── 6. Angle{N} (no preceding Distance) → Rotation ────────────
    if (/^Angle\d+$/.test(key)) {
      const current = numericValue(key);
      parameters.push({
        kind: "rotation",
        id: synId(key),
        name: key,
        label: "",
        description: "",
        chainActions: false,
        showGrip: false,
        basePoint: EMPTY_POINT,
        radius: 1,
        defaultAngle: current,
        currentAngle: current,
        angleValueSet: { ...EMPTY_VALUE_SET },
      });
      seen.add(key);
      continue;
    }

    // ── 7. Rotation{N} ────────────────────────────────────────────
    if (/^Rotation\d+$/.test(key)) {
      const current = numericValue(key);
      parameters.push({
        kind: "rotation",
        id: synId(key),
        name: key,
        label: "",
        description: "",
        chainActions: false,
        showGrip: false,
        basePoint: EMPTY_POINT,
        radius: 1,
        defaultAngle: current,
        currentAngle: current,
        angleValueSet: { ...EMPTY_VALUE_SET },
      });
      seen.add(key);
      continue;
    }

    // ── 8. Lookup{N} ──────────────────────────────────────────────
    if (/^Lookup\d+$/.test(key)) {
      const current = stringValue(key) || "Default";
      // Lookup requires a lookupTableId pointing into
      // `dynamicMetadata.lookupTables`. Without an authored table we
      // synthesise a placeholder id so zod's `.min(1)` passes; the
      // command-engine `paramInfoFromDynamicParameter` returns an
      // empty choices list when the table is missing, which is
      // exactly what we want — the Inspector shows a single option
      // (the current value) and the user can still type a custom
      // value via the underlying flow.
      parameters.push({
        kind: "lookup",
        id: synId(key),
        name: key,
        label: "",
        description: "",
        chainActions: false,
        showGrip: false,
        position: EMPTY_POINT,
        reverseLookup: false,
        lookupTableId: "syn-lookup",
        currentLookupRowId: current,
      });
      seen.add(key);
      continue;
    }

    // ── 9. Unknown — type-driven fallback ─────────────────────────
    // Numeric → Linear (so the slider renders); Boolean → Flip
    // (checkbox); String values stay un-synthesised so the Inspector's
    // raw-mode label translation path handles them.
    const value = props[key];
    if (typeof value === "number") {
      parameters.push({
        kind: "linear",
        id: synId(key),
        name: key,
        label: "",
        description: "",
        chainActions: false,
        showGrip: false,
        basePoint: EMPTY_POINT,
        secondPoint: EMPTY_POINT,
        defaultDistance: value,
        currentDistance: value,
        valueSet: { ...EMPTY_VALUE_SET },
      });
      seen.add(key);
      continue;
    }
    if (typeof value === "boolean") {
      parameters.push({
        kind: "flip",
        id: synId(key),
        name: key,
        label: "",
        description: "",
        chainActions: false,
        showGrip: false,
        axisStart: EMPTY_POINT,
        axisEnd: EMPTY_POINT,
        defaultValue: value,
        currentValue: value,
        flipLabel: "Flip",
        baseLabel: "Base",
      });
      seen.add(key);
      continue;
    }
    // String-typed unknown → leave un-synthesised. The Inspector still
    // surfaces the row via listDynamicParameters' fallback branch,
    // and the L.3 translateRawLabel path provides a localized label.
    seen.add(key);
  }

  return {
    version: 1,
    parameters,
    actions: [],
    evaluationGraph: { edges: [], chainGroups: [] },
    lookupTables: [],
    geometricConstraints: [],
    grips: [],
    futureExtensions: {},
  };
}
